import styled from 'styled-components';
import { ThemeMode } from '../../../global-state/themeContext';
import { cardShadow, Colors, muiTheme } from '../../../theme';
import { getSpacing } from '../../../utils';
import { Cursor, FlexDirections, JustifyContent, StyledContainerProps, Wrap } from './ContainerV2';

const directionMap: Record<FlexDirections, string> = {
  row: 'row',
  rowReverse: 'row-reverse',
  column: 'column',
  columnReverse: 'column-reverse',
};

const justifyContentMap: Record<JustifyContent, string> = {
  center: 'center',
  end: 'end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
  start: 'start',
};

const wrapMap: Record<Wrap, string> = {
  wrap: 'wrap',
  nowrap: 'nowrap',
  wrapReverse: 'wrap-reverse',
};

const CursorMap: Record<Cursor, string> = {
  pointer: 'pointer',
  default: 'default',
  none: 'none',
  notAllowed: 'not-allowed',
};

const getDefaultBorder = (mode: ThemeMode, borderColor?: Colors) => {
  if (borderColor) {
    return `1px solid ${muiTheme.color[borderColor]}`;
  }
  return `1px solid ${mode === 'dark' ? muiTheme.color.borderGrayDark : muiTheme.color.borderGray}`;
};

export const StyledContainer = styled.div<StyledContainerProps>`
  padding: ${({ padding, theme: { spacing } }) => padding && getSpacing(padding, spacing)};
  margin: ${({ margin, theme: { spacing } }) => margin && getSpacing(margin, spacing)};
  height: ${({ height, theme: { spacing } }) =>
    typeof height === 'string' ? height : height && getSpacing(height, spacing)};
  width: ${({ width, theme: { spacing } }) =>
    typeof width === 'string' ? width : width && getSpacing(width, spacing)};
  max-width: ${({ maxWidth, theme: { spacing } }) =>
    typeof maxWidth === 'string' ? maxWidth : maxWidth && getSpacing(maxWidth, spacing)};
  min-width: ${({ minWidth, theme: { spacing } }) =>
    typeof minWidth === 'string' ? minWidth : minWidth && getSpacing(minWidth, spacing)};
  max-height: ${({ maxHeight, theme: { spacing } }) =>
    typeof maxHeight === 'string' ? maxHeight : maxHeight && getSpacing(maxHeight, spacing)};
  min-height: ${({ minHeight, theme: { spacing } }) =>
    typeof minHeight === 'string' ? minHeight : minHeight && getSpacing(minHeight, spacing)};
  display: ${({ display }) => display && display};
  flex-direction: ${({ flexDirection }) => directionMap[flexDirection || 'row']};
  gap: ${({ gap, theme: { spacing } }) => gap && getSpacing(gap, spacing)};
  justify-content: ${({ justifyContent }) => justifyContentMap[justifyContent || 'start']};
  align-items: ${({ alignItems }) => alignItems && alignItems};
  flex-wrap: ${({ flexWrap }) => wrapMap[flexWrap || 'nowrap']};
  background: ${({ backgroundColor, theme: { color } }) => backgroundColor && color[backgroundColor]};
  border: ${({ borderColor, hasBorder, theme: { palette } }) =>
    hasBorder && getDefaultBorder(palette.mode, borderColor)};
  position: ${({ position }) => (position ? position : 'static')};
  top: ${({ top }) => top && top};
  left: ${({ left }) => left && left};
  right: ${({ right }) => right && right};
  bottom: ${({ bottom }) => bottom && bottom};
  overflow: ${({ overflow }) => overflow && 'auto'};
  cursor: ${({ cursor }) => cursor && CursorMap[cursor]};
  opacity: ${({ opacity }) => opacity && opacity};
  text-align: ${({ textAlign }) => textAlign && textAlign};
  align-content: ${({ alignContent }) => alignContent && alignContent};
  align-self: ${({ alignSelf }) => alignSelf && alignSelf};
  border-radius: ${({ borderRadius, theme: { spacing } }) => borderRadius && getSpacing(borderRadius, spacing)};
  flex: ${({ flex }) => flex && flex};
  ${({ shadow }) => shadow && cardShadow()}
`;
